import React, { FC, useContext, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { InfoMessage, WarningMessage } from '@eika/attention-message';
import { ButtonTypes, StandardButton } from '@eika/button';
import { CheckmarkList } from '@eika/checkmark-list';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { AppContext } from '../../containers/appContextContainer';
import {
    GjeldAksjonWeb,
    GjeldKildeWeb,
    GjeldTypeWeb,
    GjeldWeb,
    SoeknadstypeWeb,
    StatusWeb,
    ValgtWeb,
} from '../../generated-types';
import useGjeld, { useDeleteGjeld, useUpdateGjeld } from '../../hooks/useGjeld';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import Fellesgjeld from './fellesgjeld';
import GjeldBank, { isBankGjeld, isKundeGjeld } from './gjeldBank';
import GjeldKunde, { newGjeld } from './gjeldKunde';
import GjeldSum from './gjeldSum';
import GjeldValg from './gjeldValg';
import Gjeldsregister from './gjeldsregister';
import Salgspant from './salgspant';

export type GjeldFormData = {
    harGjeld: ValgtWeb;
    gjeld: {
        aksjon: GjeldAksjonWeb;
        gjeldId: string | null;
        kilde: GjeldKildeWeb | null;
        kredittramme: number | undefined;
        laangiver: string;
        restgjeld: number | undefined;
        sikret: boolean;
        status: StatusWeb;
        gjeldType: GjeldTypeWeb;
        skalInnfrisValg: ValgtWeb;
        maanedligeFellesutgifter: number | null;
        skalViseFellesutgifter: boolean;
        pantebeloep: number | null;
        registrertDato: string | null;
        registreringsnummer: string | null;
    }[];
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
    navigateFromOption?: string;
}

const Gjeld: FC<Props> = ({ navigateTo, navigateFrom, navigateFromOption = null }) => {
    const { gjeld, button } = useTranslation();
    const navigate = useNavigate();
    const { soeknadstype } = useContext(AppContext);
    const { data: gjeldContainer, isLoading } = useGjeld(soeknadstype);
    const methods = useForm<GjeldFormData>({ defaultValues: { harGjeld: ValgtWeb.IKKE_VALGT, gjeld: [] } });
    const {
        control,
        reset,
        handleSubmit,
        formState: { errors },
    } = methods;
    const { fields, append, remove } = useFieldArray({ name: 'gjeld', control });
    const { mutate, isPending: isUpdatingGjeld } = useUpdateGjeld(gjeldContainer, () => {
        navigate(navigateTo);
    });
    const { mutate: deleteGjeld } = useDeleteGjeld(gjeldContainer);
    const [infriddGjeld, setInfriddGjeld] = useState<GjeldWeb[]>([]);

    const nyRestGjeldKunde = (
        gjeldType: GjeldTypeWeb,
        restgjeldNy?: number,
        restgjeldGammel?: number,
    ): number | null => {
        if (gjeldType === GjeldTypeWeb.STUDIELAAN && restgjeldNy !== restgjeldGammel) {
            return restgjeldNy ?? null;
        }
        return [GjeldTypeWeb.BOLIGLAAN, GjeldTypeWeb.BILLAAN, GjeldTypeWeb.ANNET].includes(gjeldType)
            ? restgjeldNy ?? null
            : null;
    };

    useEffect((): void => {
        if (gjeldContainer?.gjeld) {
            const infriddGjeld: GjeldWeb[] = [];
            const gjeldForm: GjeldFormData = { harGjeld: gjeldContainer.harGjeld, gjeld: [] };
            gjeldContainer.gjeld.forEach((g) => {
                if (
                    g.restgjeld > 0 ||
                    (g.restgjeldKunde && g.restgjeldKunde > 0) ||
                    (g.kredittramme && g.kredittramme > 0)
                ) {
                    gjeldForm.gjeld.push({
                        aksjon: g.aksjon,
                        gjeldId: g.id,
                        kilde: g.kilde,
                        kredittramme: g.kredittramme || 0,
                        laangiver: g.laangiver,
                        restgjeld: g.restgjeldKunde ?? g.restgjeld,
                        sikret: g.sikret,
                        status: g.status,
                        gjeldType: g.type,
                        skalInnfrisValg: ValgtWeb.IKKE_VALGT,
                        maanedligeFellesutgifter: g.maanedligeFellesutgifter,
                        skalViseFellesutgifter: g.eiere.length < 2,
                        pantebeloep: g.pantebeloep,
                        registrertDato: g.registrertDato,
                        registreringsnummer: g.registreringsnummer,
                    });
                } else {
                    g.skalInnfris = false;
                    g.status = StatusWeb.CONFIRMED;
                    infriddGjeld.push(g);
                }
            });
            setInfriddGjeld(infriddGjeld);
            reset(gjeldForm);
        }
    }, [gjeldContainer, reset]);

    const onSubmitGjeld = (data: GjeldFormData) => {
        if (gjeldContainer && data.gjeld.length > 0) {
            const gjeldList: GjeldWeb[] = infriddGjeld;
            data.gjeld.forEach((g1) => {
                const gjeld = gjeldContainer.gjeld.find((g2) => g2.id === g1.gjeldId);
                if (gjeld) {
                    gjeld.laangiver = g1.laangiver;
                    gjeld.restgjeldKunde = nyRestGjeldKunde(g1.gjeldType, g1.restgjeld, gjeld.restgjeld) ?? null;
                    gjeld.skalInnfris = g1.aksjon === GjeldAksjonWeb.ERSTATTES;
                    gjeld.status = StatusWeb.CONFIRMED;
                    gjeld.type = g1.gjeldType;
                    gjeld.maanedligeFellesutgifter = g1.maanedligeFellesutgifter;
                    gjeldList.push(gjeld);
                } else {
                    gjeldList.push({
                        aksjon: g1.aksjon,
                        eiere: [gjeldContainer.laantaker],
                        id: null,
                        kilde: g1.kilde,
                        kredittramme: g1.kredittramme || 0,
                        laangiver: g1.laangiver,
                        restgjeld: 0,
                        restgjeldKunde: g1.restgjeld ?? 0,
                        sikret: false,
                        skalInnfris: g1.aksjon === GjeldAksjonWeb.ERSTATTES,
                        status: StatusWeb.CONFIRMED,
                        type: g1.gjeldType,
                        maanedligeFellesutgifter: g1.maanedligeFellesutgifter,
                        pantebeloep: null,
                        registrertDato: null,
                        registreringsnummer: null,
                    });
                }
            });
            mutate({
                soeknadId: gjeldContainer?.soeknadId,
                soeknadStatus: gjeldContainer?.soeknadStatus,
                soeknadstype: gjeldContainer?.soeknadstype,
                laantaker: gjeldContainer.laantaker,
                harGjeld: data.harGjeld,
                gjeld: gjeldList,
                gjeldsregisterFeilet: gjeldContainer.gjeldsregisterFeilet,
            });
        } else {
            navigate(navigateTo);
        }
    };

    const onClickHarGjeld = (valg: ValgtWeb) => {
        if (valg === ValgtWeb.JA && fields.length === 0) {
            append(newGjeld);
        } else if (valg === ValgtWeb.NEI) {
            const gjeldIder: string[] = [];
            fields.forEach((f) => {
                if (f.gjeldId !== null && f.kilde === GjeldKildeWeb.KUNDE) {
                    gjeldIder.push(f.gjeldId);
                }
            });
            if (gjeldContainer?.soeknadId && gjeldIder.length > 0 && gjeldIder[0] !== '') {
                deleteGjeld(
                    { soeknadId: gjeldContainer?.soeknadId, gjeldIder },
                    {
                        onSuccess: () => {
                            remove();
                        },
                    },
                );
            } else {
                remove();
            }
        }
    };

    const onClickLeggTilGjeld = () => {
        append(newGjeld);
    };

    const onClickSlett = (index: number) => {
        const { gjeldId } = fields[index];
        if (gjeldContainer?.soeknadId && gjeldId) {
            deleteGjeld(
                { soeknadId: gjeldContainer?.soeknadId, gjeldIder: [gjeldId] },
                {
                    onSuccess: () => {
                        remove(index);
                    },
                },
            );
        } else {
            remove(index);
        }
    };

    const onForrigeClick = () => {
        if (
            navigateFromOption &&
            (gjeldContainer?.soeknadstype === SoeknadstypeWeb.FINANSIERINGSBEVIS ||
                gjeldContainer?.soeknadstype === SoeknadstypeWeb.KJOEPE_BOLIG)
        ) {
            navigate(navigateFromOption);
        } else {
            navigate(navigateFrom);
        }
    };

    return (
        <PageCard title={gjeld.title}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitGjeld)}>
                    <div className="row row-gap-large">
                        <span className="typography-ingress__light">{gjeld.subTitle}</span>
                        {gjeldContainer?.gjeldsregisterFeilet && (
                            <WarningMessage
                                id="gjeldWarning"
                                text={gjeld.infoGjeldsregisterError}
                                icon={IconTypes.UTROPSTEGN_24}
                            />
                        )}
                        <div>
                            <div className="margin-bottom-small">{`${gjeld.infoTitle}`}</div>
                            <CheckmarkList
                                checkmarks={[
                                    {
                                        label: gjeld.infoGjeldIBank,
                                        checked: true,
                                    },
                                    {
                                        label: gjeld.infoUsikretGjeld,
                                        checked: true,
                                    },
                                    {
                                        label: gjeld.infoStudielaan,
                                        checked: true,
                                    },
                                    {
                                        label: gjeld.infoAndelFellesgjeld,
                                        checked: true,
                                    },
                                    {
                                        label: gjeld.infoBillaan,
                                        checked: true,
                                    },
                                ]}
                            />
                        </div>
                        {isLoading && <Loader />}
                        {gjeldContainer?.gjeld && isKundeGjeld(gjeldContainer.gjeld) && (
                            <GjeldValg onClickHarGjeld={onClickHarGjeld} />
                        )}
                        <div>
                            {fields.map((field, index) => {
                                return (
                                    <div key={field.id}>
                                        {field.kilde && isBankGjeld(field.kilde) && (
                                            <GjeldBank index={index} control={control} />
                                        )}
                                        {(field.kilde === GjeldKildeWeb.GJELDSREGISTER ||
                                            field.kilde === GjeldKildeWeb.SKATTEETATEN) && (
                                            <Gjeldsregister index={index} control={control} errors={errors} />
                                        )}
                                        {field.kilde === GjeldKildeWeb.KUNDE && (
                                            <GjeldKunde index={index} onClickSlett={onClickSlett} />
                                        )}
                                        {field.kilde === GjeldKildeWeb.EIENDOMSVERDI && (
                                            <Fellesgjeld index={index} control={control} errors={errors} />
                                        )}
                                        {field.kilde === GjeldKildeWeb.BISNODE_SALGSPANT && (
                                            <Salgspant index={index} control={control} errors={errors} />
                                        )}
                                    </div>
                                );
                            })}
                            {fields.length > 0 && (
                                <StandardButton
                                    icon={IconTypes.PLUSS_36}
                                    buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                    className="max-width-200"
                                    onClick={onClickLeggTilGjeld}
                                >
                                    {button.leggTilGjeld}
                                </StandardButton>
                            )}
                        </div>
                        <GjeldSum />
                        {gjeldContainer?.gjeld.some((g) => g.kilde === GjeldKildeWeb.GJELDSREGISTER) && (
                            <InfoMessage text={gjeld.infoGjeldsregister} icon={IconTypes.INFO_36} type="default" />
                        )}
                    </div>
                    <PageFooter
                        soeknadId={gjeldContainer?.soeknadId}
                        onForrigeClick={onForrigeClick}
                        showSpinner={isUpdatingGjeld}
                    />
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default Gjeld;
